import React, { useEffect } from 'react'
import { useSession } from 'src/sdk/session'
import { useInView } from 'react-intersection-observer'
import { usePromoView } from 'src/sdk/analytics/hooks/usePromoViewEvent'
import { usePromoClick } from 'src/sdk/analytics/hooks/usePromoClickEvent'
import { EventPromoClick } from 'src/components/common/EventPromoClick'
import { getCookie, setCookie } from 'src/utils/cookie'
import { Link } from 'gatsby'
import { ImageWithArtDirection } from 'src/components/ui/Image'

import type { Item } from './AlignTextFullwidthCarousel'
import BlockTextFullwidthCarousel from './BlockTextFullwidthCarousel'

type DynamicImageProps = {
  index: number
  item: Item & {
    altData: string
  }
  isMobile: boolean
  desktop: {
    sourceWidth?: number | undefined
    sourceHeight?: number | undefined
    aspectRatio: number
    baseUrl: string
  }
  mobile: {
    sourceWidth?: number | undefined
    sourceHeight?: number | undefined
    baseUrl: string
  }
  setAltForEvent: React.Dispatch<React.SetStateAction<string>>
  onPromoView: (promotionId: string) => void
  promoViewEventTriggered: Set<string>
  imgOptions: {
    loading: 'eager'
    layout: 'constrained'
  }
  isTextBlock:
    | 'slider-fullwidthCarousel-right-text'
    | 'slider-fullwidthCarousel-left-text'
    | 'slider-fullwidthCarousel-text-mobile'
    | ''
  textcolorbutton: string
  componentLayout: string | undefined
}

function DynamicImageWithText({
  index,
  item,
  isMobile,
  setAltForEvent,
  onPromoView,
  promoViewEventTriggered,
  mobile,
  desktop,
  imgOptions,
  isTextBlock,
  textcolorbutton,
  componentLayout,
}: DynamicImageProps) {
  const { person } = useSession()
  const { sendPromoViewEvent } = usePromoView()
  const { sendPromoClickEvent } = usePromoClick()
  const uuidToUse = getCookie('uuid')

  const { ref: inViewRef, inView } = useInView({
    triggerOnce: true,
    threshold: 0.75,
  })

  const {
    promotion,
    campaignEco,
    promotionCategory,
    promotionName,
    promotionPosition,
    promotionId,
  } = item

  useEffect(() => {
    if (
      promotion &&
      inView &&
      promotionId &&
      !promoViewEventTriggered.has(promotionId)
    ) {
      sendPromoViewEvent({
        campaign_eco: campaignEco,
        promotion_id: promotionId as string,
        promotion_category: promotionCategory as string,
        promotion_name: promotionName as string,
        promotion_creative: item.alt,
        promotion_position: promotionPosition as string,
        page_location: window.location.href,
        user_logged: !!person?.id,
        user_id: person?.id ?? null,
        uuid: uuidToUse,
      })

      if (campaignEco) {
        setCookie('campanhaEco', campaignEco, 7, '.vivara.com.br')
      }

      onPromoView(promotionId)
    }
  }, [inView, promotion, promoViewEventTriggered, promotionId, onPromoView])

  return (
    <div
      key={index}
      ref={inViewRef}
      className={`slider-fullwidthCarousel-container ${isTextBlock} `}
    >
      <Link
        to={item?.href}
        className="slider-fullwidthCarousel-align-link"
        draggable
        onClick={() => {
          EventPromoClick(
            item?.alt,
            isMobile ? mobile.baseUrl : desktop.baseUrl,
            index
          )

          if (promotion) {
            sendPromoClickEvent({
              campaign_eco: campaignEco,
              promotion_id: promotionId as string,
              promotion_category: promotionCategory as string,
              promotion_name: promotionName as string,
              promotion_creative: item.alt,
              position: promotionPosition as string,
              user_logged: !!person?.id,
              user_id: person?.id ?? null,
              uuid: uuidToUse,
            })

            if (campaignEco) {
              setCookie('campanhaEco', campaignEco, 7, '.vivara.com.br')
            }
          }
        }}
      >
        <ImageWithArtDirection
          desktop={desktop}
          mobile={mobile}
          alt={`${item?.alt}${index}`}
          altData={item?.altData}
          setAltForEvent={setAltForEvent}
          {...imgOptions}
        />
        <BlockTextFullwidthCarousel
          title={item?.textBlock?.[0]?.title ?? ''}
          subtitle={item?.textBlock?.[0]?.subtitle ?? ''}
          btnText={item?.textBlock?.[0]?.btnText ?? ''}
          btnBackground={item?.textBlock?.[0]?.btnBackground ?? ''}
          btnColorText={item?.textBlock?.[0]?.btnColorText ?? ''}
          btnBorder={item?.textBlock?.[0]?.btnBorder ?? ''}
          btnLink={item?.textBlock?.[0]?.btnLink ?? ''}
          textColor={textcolorbutton ?? ''}
          componentLayout={componentLayout ?? ''}
        />
      </Link>
    </div>
  )
}

DynamicImageWithText.displayName = 'DynamicImageWithText'

export default DynamicImageWithText
